import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { useNavigate } from "react-router";
import { Spin, Tooltip } from "antd";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import {
  useInvestigatorSitesQuery,
  type InvestigatorSite,
} from "@/hooks/useInvestigatorSitesQuery";
import mapMarker from "@/assets/images/map_marker.svg";
import SiteLocationsTable from "./SiteLocationsTable";

const markerIcon = new L.Icon({
  iconUrl: mapMarker,
  iconRetinaUrl: mapMarker,
  iconAnchor: [15, 45],
  iconSize: [30, 50],
});

const createClusterIcon = (cluster: any) =>
  L.divIcon({
    html: `<span class="bg-[#2B4752] w-[36px] h-[36px] rounded-full flex items-center justify-center border-2 border-white font-bold text-white text-[13px] shadow-md">${cluster.getChildCount()}</span>`,
    className: "customMarker",
    iconSize: L.point(36, 36, true),
  });

type MappableSite = InvestigatorSite & { lat: number; lon: number };

interface SiteLocationsTabProps {
  invKey: string;
}

export default function SiteLocationsTab({ invKey }: SiteLocationsTabProps) {
  const { buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const profileHidden = useFeatureFlag(FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN);

  const asTable = useFeatureFlag(FEATURE_FLAGS.PROFILE_SIMPLIFIED);

  const { data: sites = [], isLoading } = useInvestigatorSitesQuery(invKey);

  const mappableSites = useMemo(
    () =>
      sites.filter(
        (site): site is MappableSite => site.lat != null && site.lon != null,
      ),
    [sites],
  );

  const center = useMemo((): [number, number] => {
    if (!mappableSites.length) return [20, 0];
    const avgLat =
      mappableSites.reduce((s, p) => s + p.lat, 0) / mappableSites.length;
    const avgLon =
      mappableSites.reduce((s, p) => s + p.lon, 0) / mappableSites.length;
    return [avgLat, avgLon];
  }, [mappableSites]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin />
      </div>
    );
  }

  if (!sites.length) {
    return (
      <div className="flex items-center justify-center h-64 text-[#8C8C8C] text-[13px]">
        No site location data available for this investigator.
      </div>
    );
  }

  if (asTable) {
    return (
      <div className="flex flex-col gap-3">
        <div className="text-[12px] text-[#8C8C8C]">
          {sites.length} site{sites.length !== 1 ? "s" : ""}
        </div>
        <SiteLocationsTable sites={sites} />
      </div>
    );
  }

  const unmappedCount = sites.length - mappableSites.length;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[12px] text-[#8C8C8C]">
        {sites.length} site{sites.length !== 1 ? "s" : ""}
        {unmappedCount > 0 &&
          ` · ${unmappedCount} without map coordinates not shown`}
      </div>
      <div
        className="rounded-[8px] overflow-hidden border border-[#F0F2F4]"
        style={{ height: 520 }}
      >
        <MapContainer
          center={center}
          zoom={mappableSites.length === 1 ? 8 : 3}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />
          <MarkerClusterGroup iconCreateFunction={createClusterIcon}>
            {mappableSites.map((site) => (
              <Marker
                key={site.site_key}
                position={[site.lat, site.lon]}
                icon={markerIcon}
              >
                <Popup>
                  <div className="flex flex-col gap-1 min-w-[160px]">
                    {site.site_key && !profileHidden ? (
                      <button
                        type="button"
                        className="text-left text-[13px] font-semibold text-[#2B4752] hover:text-[#0C98D0] bg-transparent border-0 p-0 cursor-pointer"
                        onClick={() =>
                          navigate(buildUrl(`site-profiles/${site.site_key}`))
                        }
                      >
                        {site.site_name ?? site.site_key}
                      </button>
                    ) : (
                      <Tooltip title="No site profile available for this location">
                        <span className="text-left text-[13px] font-semibold text-[#2B4752] cursor-default">
                          {site.site_name ?? site.site_key}
                        </span>
                      </Tooltip>
                    )}
                    {site.city && (
                      <span className="text-[11px] text-[#6B7280]">
                        {[site.city, site.country_code]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                    <span className="text-[11px] text-[#8C8C8C]">
                      {site.trial_count} trial
                      {site.trial_count !== 1 ? "s" : ""}
                    </span>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}
